
import { Language } from '../types';

// Define individual language translations to maintain clarity and satisfy Record<Language, any>
const en = {
  nav: {
    dashboard: 'Dashboard',
    findMentor: 'Find Teacher',
    bookings: 'My Bookings',
    subscriptions: 'Subscriptions',
    homework: 'Homework',
    messages: 'Messages',
    wallet: 'Wallet',
    profile: 'Profile',
    signOut: 'Sign Out'
  },
  common: {
    loading: 'Loading...',
    back: 'Back',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    processing: 'Processing...',
    actions: 'Actions',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    mentor: 'Teacher',
    student: 'Student',
    next: 'Next',
    submit: 'Submit',
    viewAll: 'View All',
    enterClass: 'Enter Class'
  },
  auth: {
    login: 'Log In',
    register: 'Sign Up',
    emailLabel: 'Your Email',
    passwordLabel: 'Password',
    fullNameLabel: 'Full Name',
    forgotPassword: 'Forgot Password?',
    enterSystem: 'Enter System',
    readme: 'System Documentation (README)',
    registerSuccess: 'Register successful! Redirecting...',
    loginFailed: 'Login failed. Please try again.',
    emailNotExists: 'Email does not exist.',
    quickAccess: 'Development Access',
    quickAccessTitle: 'Quick Access',
    newTeammate: 'New Teammate?',
    administrators: 'Administrators',
    otherRoles: 'Other Roles',
    adminLabel: 'Admin',
    resetData: 'Reset System Data',
    resetConfirm: 'Are you sure you want to clear all data and re-initialize mock accounts?',
    chooseRole: 'I want to register as...',
    menteeRole: 'Student',
    mentorRole: 'Teacher',
    providerRole: 'Affiliate Provider',
    completeRegister: 'Complete Registration'
  },
  mentee: {
    welcome: 'Welcome back',
    readyToLearn: 'Are you ready for your next English lesson?',
    creditsRemaining: 'credits remaining',
    bookLesson: 'Book a Lesson',
    nextLesson: 'Next Lesson',
    noUpcoming: 'No upcoming lessons scheduled.',
    viewDetails: 'View Details',
    weeklyGoal: 'Weekly Goal',
    lessons: 'Lessons',
    homework: 'Homework',
    pending: 'Pending',
    streak: 'Streak',
    days: 'Days',
    homeworkTitle: 'Homework & Assignments',
    noHomework: 'No homework assigned yet.',
    allDone: 'All done!',
    submit: 'Submit',
    wallet: 'My Wallet',
    availableBalance: 'Available Balance',
    topUp: 'Top Up',
    transactionHistory: 'Transaction History',
    noTransactions: 'No transactions yet.',
    liveSupport: 'Live Support',
    credits: 'Credits',
    creditsLabel: 'Credits',
    creditsDescription: '* Credits are used for 1:1 sessions.',
    
    findMentorTitle: 'Find a Teacher',
    findMentorSubtitle: 'Browse and book sessions with top English experts.',
    searchPlaceholder: 'Search name or tag...',
    filters: 'Filters',
    viewProfile: 'View Profile',
    perHour: '/ hour',

    myBookings: 'My Bookings',
    buySubscription: 'Buy Subscription',
    bookNewLesson: 'Book New Lesson',
    noBookingsTitle: 'No bookings yet',
    noBookingsDesc: 'Start your journey by finding a teacher.',
    scheduled: 'Scheduled',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled',
    tabUpcoming: 'Upcoming',
    tabHistory: 'History',
    tabCancelled: 'Cancelled',
    bookingsEmptyDesc: 'Your scheduled lessons will appear here.',
    joinDetails: 'Join / Details',
    rate: 'Rate',
    underReview: 'Under Review',
    reportSubmitted: 'Report submitted. Our team will review it shortly.',
    allLevels: 'All Levels',
    allTopics: 'All Topics',
    mentorsAvailable: '{count} teachers available',
    recommended: 'Recommended',
    noMentorsFound: 'No teachers found',
    noMentorsDesc: 'We couldn\'t find any teachers matching your current filters.',
    bookingCancelledSuccess: 'Booking cancelled successfully. Sessions or credits have been returned.',
    rescheduledSuccess: 'Rescheduled successfully.',
    loadingLessonDetails: 'Loading lesson details...',
    backToBookings: 'Back to My Bookings',
    lessonSummary: 'Lesson Summary',
    lessonDate: 'Lesson Date',
    timeSlot: 'Time Slot',
    sessionsLeft: 'Sessions Left',
    assignmentLabel: 'Assignment for this lesson',
    openTask: 'Open Task',
    result: 'Result',
    enterClassroom: 'Enter Online Classroom',
    reschedule: 'Reschedule',
    cancelLesson: 'Cancel Lesson',
    leaveReview: 'Leave a Review',
    pricingCalculationError: 'Unable to calculate localized pricing.',
    loadingProfile: 'Loading profile...',
    mentorNotFound: 'Teacher not found.',
    yourRate: "Teacher's Rate",
    creditsPerSession: 'Credits per session',
    bookNow: 'Book Now',
    aboutMe: 'About Me',
    mentorSchedule: 'Teacher Schedule',
    timezoneConversion: 'Times converted to your local zone ({tz})',
    activeSubscription: 'Active Subscription',
    planLabel: 'Plan',
    mentorLabel: 'Teacher: ',
    expiresLabel: 'Expires: ',
    activeStatus: 'ACTIVE',
    subscriptionFailed: 'Failed to subscribe: ',
    loadingSubscription: 'Loading subscription...',
    noActiveSubscription: 'No Active Subscription',
    payAsYouGoPlan: 'You are currently on the pay-as-you-go plan.',
    browsePlans: 'Browse Plans',
    usageStats: 'Usage Stats',
    totalSessions: 'Total Sessions',
    remaining: 'Remaining',
    viewSchedule: 'View Schedule',
    subscriptionDetailTitle: 'Subscription Details',
    
    upgradeTitle: 'Upgrade to Premium',
    upgradeDesc: 'Commit to your learning journey with a monthly plan. Get consistent practice and save up to 20% compared to credit packs.',
    premiumLearning: 'Premium Learning',
    whySubscribe: 'Why choose a subscription?',
    benefitTitle1: 'Commitment Device',
    benefitDesc1: 'Build a consistent habit with fixed weekly schedules. No more putting off lessons.',
    benefitTitle2: 'Better Rates',
    benefitDesc2: 'Save up to 20% compared to booking single lessons with credits.',
    benefitTitle3: 'Guaranteed Slots',
    benefitDesc3: 'Secure your favorite teacher\'s prime time slots 4 weeks in advance.',
    faqTitle: 'Frequently Asked Questions',
    faq1Question: 'Can I reschedule a lesson?',
    faq1Answer: 'Yes, depending on your plan. The Starter plan allows 1 reschedule per month. Cancellation must be done 24h in advance.',
    faq2Question: 'What happens if my teacher cancels?',
    faq2Answer: 'If a teacher cancels, the session credit is returned to your quota, and you can rebook anytime.',
    faq3Question: 'Do credits roll over?',
    faq3Answer: 'No, subscription sessions must be used within the 4-week cycle to encourage consistent practice.',
    activePlan: 'You have an active {plan} subscription',
    validUntil: 'Valid until',
    manageSubscription: 'Manage Subscription',
    choosePlan: 'Choose Plan',
    currentPlan: 'Current Plan',
    lessonsCount: '{count} Lessons',
    weeklySchedule: 'Weekly fixed schedule',
    quotaInfo: '{cancel} Cancel / {reschedule} Reschedule',
    
    subscriptionDetail: {
      backToList: 'Back to Plans',
      stepMentor: 'Choose Teacher',
      stepSchedule: 'Set Schedule',
      stepConfirm: 'Confirm',
      mentorTitle: 'Your Learning Guide',
      mentorSubtitle: 'Choose a teacher to accompany you for the next 4 weeks.',
      scheduleTitle: 'Set Your Weekly Routine',
      scheduleSubtitle: 'Choose recurring slots that fit your weekly schedule.',
      projectedSchedule: 'Projected Schedule',
      noSlots: 'No slots selected',
      recurring: 'Recurring',
      nextStep: 'Summary & Payment',
      summaryTitle: 'Confirm Subscription',
      summarySubtitle: 'Please review your plan details.',
      planLabel: 'Learning Plan',
      sessionsSuffix: 'Lessons / 4 weeks',
      mentorLabel: 'Partner Teacher',
      roadmapLabel: 'Next 4 Weeks Roadmap',
      totalPrice: 'Total Price',
      walletBalance: 'Current Wallet',
      confirmAndStart: 'Confirm & Start Learning',
      insufficientTitle: 'Insufficient Balance',
      insufficientDesc: 'You need {needed} more Credits to complete this registration.',
      topUpNow: 'Top up now'
    },

    calendar: {
      title: 'Choose {count} fixed slots per week',
      selectedCount: 'Selected: {current} / {max}',
      note: '* Green slots indicate teacher availability. Choose recurring weekly times.'
    },

    activeSubscriptionTitle: 'Active Subscription',
    subscriptionLimits: 'Subscription Limits',
    cancelsLeft: 'Cancels Left',
    reschedulesLeft: 'Reschedules Left',
    resetsOn: 'Resets on',
    noActiveSub: 'No Active Subscription',
    payAsYouGo: 'You are currently on the pay-as-you-go plan.',

    profile: {
      myProfile: 'My Profile',
      settings: 'Settings',
      generalInformation: 'General Information',
      security: 'Security',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      updatePassword: 'Update Password',
      changeAvatar: 'Click camera icon to change',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      country: 'Country'
    },

    walletModal: {
      title: 'Top Up Credits',
      paymentTotal: 'Total Payment',
      securePayment: 'Secure SSL Payment',
      payNow: 'Pay Now',
      currency: 'Currency',
      selectCreditPackage: '1. Select Credit Package',
      credits: 'credits',
      selectPaymentMethod: '2. Select Payment Method',
      noPaymentMethods: 'No payment methods available. Please contact admin.',
      choosePaymentMethod: 'Choose payment method...',
      completePayment: '3. Complete Payment',
      totalAmount: 'Total Amount',
      qrCode: 'QR Code',
      bank: 'Bank',
      account: 'Account',
      name: 'Name',
      transactionCode: 'Transaction Code',
      transactionCodePlaceholder: 'Enter transaction code from your payment',
      transactionCodeHint: 'Enter the transaction code from your {method} payment',
      processing: 'Processing...',
      confirmPayment: 'Confirm Payment',
      fillAllFields: 'Please fill in all required fields',
      transactionCodeTooShort: 'Transaction code must be at least 6 characters',
      failedLoadMethods: 'Failed to load payment methods',
      failedCreateTopup: 'Failed to create topup'
    },

    homeworkModal: {
      placeholder: 'Write your notes or answers here...',
      dropzone: 'Drag and drop files here',
      submitNow: 'Submit Now',
      submittedTitle: 'Assignment Submitted',
      submittedDesc: 'You submitted this on {date}. Waiting for feedback.'
    }
  },
  mentor: {
    welcome: 'Hello Teacher',
    upcomingLessons: 'upcoming lessons',
    availableCredits: 'Available Credits',
    manageSchedule: 'Manage Schedule',
    openMoreSlots: 'Open more time slots for students to book.',
    mentorTip: 'Teacher Tip',
    mentorTipDesc: 'Open your schedule at least 2 weeks in advance so students can easily plan their timetable.',
    refreshCalendar: 'Refresh calendar',
    available: 'Available',
    booked: 'Booked Class',
    completed: 'Completed',
    earningsManagement: 'Earnings Management',
    pending: 'Pending',
    fromUpcomingClasses: 'From upcoming classes',
    payable: 'Payable',
    canRequestPayout: 'Can request withdrawal',
    paid: 'Paid',
    lifetimeEarnings: 'Lifetime total earnings',
    recentEarningsHistory: 'Recent Earnings History',
    date: 'Date',
    student: 'Student',
    amount: 'Amount',
    status: 'Status',
    credited: 'Credited',
    adminSupport: 'Admin Support',
    assignedHomework: 'Assigned Homework',
    manageGradeHomework: 'Manage and grade student homework here.',
    slotRegistered: 'Schedule registered successfully!',
    errorSavingSlot: 'Error saving schedule: ',
    slotUpdated: 'Schedule updated.',
    errorUpdating: 'Error updating: ',
    slotDeleted: 'Schedule deleted.',
    errorDeleting: 'Error deleting: '
  },
  notifications: {
    commissionEarned: 'Commission Earned!',
    commissionMessage: (amount: number, studentName: string, topupAmount: number) =>
      `You earned ${amount} Cr commission from ${studentName}'s ${topupAmount} Cr top-up.`,
    accountActivated: 'Account Activated',
    accountActivatedMessage: 'Your account has been activated. You can now use all platform features.',
    accountSuspended: 'Account Suspended',
    accountSuspendedMessage: (reason?: string) =>
      `Your account has been suspended${reason ? `: ${reason}` : '.'}`,
    pendingApproval: 'Pending Approval',
    pendingApprovalMessage: 'Your account is pending approval. We will notify you once it\'s reviewed.',
    accountRejected: 'Account Rejected',
    accountRejectedMessage: (reason?: string) =>
      `Your account application was rejected${reason ? `: ${reason}` : '.'}`,
    newBooking: 'New Booking!',
    newBookingMessage: (studentName: string, startTime: string) =>
      `${studentName} booked a lesson at ${new Date(startTime).toLocaleString()}`,
    lessonCompleted: 'Lesson Completed',
    lessonCompletedMessage: (teacherName: string) =>
      `Your lesson with ${teacherName} has been completed. Please leave a review!`,
    bookingCancelled: 'Booking Cancelled',
    bookingCancelledMessage: (teacherName: string) =>
      `Your booking with ${teacherName} has been cancelled. Credits have been refunded.`,
    bookingRescheduled: 'Booking Rescheduled',
    bookingRescheduledMessage: (teacherName: string) =>
      `Your lesson with ${teacherName} has been rescheduled.`,
    payoutApproved: 'Payout Approved',
    payoutApprovedMessage: (amount: number) =>
      `Your payout of ${amount} Cr has been approved and will be processed soon.`,
    payoutCompleted: 'Payout Completed',
    payoutCompletedMessage: (amount: number) =>
      `Your payout of ${amount} Cr has been sent. Please check your payment method.`,
    payoutRejected: 'Payout Rejected',
    payoutRejectedMessage: (note?: string) =>
      `Your payout request was rejected${note ? `: ${note}` : '.'}`,
    creditsAdded: 'Credits Added',
    creditsAddedMessage: (amount: number) =>
      `${amount} credits have been added to your wallet.`,
    refundReceived: 'Refund Received',
    refundReceivedMessage: (amount: number, note?: string) =>
      `You received a refund of ${amount} credits${note ? `: ${note}` : '.'}`,
    earningsReceived: 'Earnings Received',
    earningsReceivedMessage: (amount: number) =>
      `You earned ${amount} credits from a completed lesson.`,
    creditAdjustment: 'Credit Adjustment',
    creditAdjustmentMessage: (note?: string) =>
      `Admin adjusted your balance${note ? `: ${note}` : '.'}`,
    subscriptionExpiring: 'Subscription Expiring Soon',
    subscriptionExpiringMessage: (planName: string, expiryDate: string) =>
      `Your ${planName} subscription expires on ${new Date(expiryDate).toLocaleDateString()}. Renew now to continue!`,
    justNow: 'Just now',
    minutesAgo: (minutes: number) => `${minutes}m ago`,
    hoursAgo: (hours: number) => `${hours}h ago`,
    daysAgo: (days: number) => `${days}d ago`
  }
};

const vi = {
  nav: {
    dashboard: 'Trang chủ',
    findMentor: 'Tìm Mentor',
    bookings: 'Lịch học',
    subscriptions: 'Gói học',
    homework: 'Bài tập',
    messages: 'Tin nhắn',
    wallet: 'Ví tiền',
    profile: 'Hồ sơ',
    signOut: 'Đăng xuất'
  },
  common: {
    loading: 'Đang tải...',
    back: 'Quay lại',
    confirm: 'Xác nhận',
    cancel: 'Hủy',
    save: 'Lưu',
    processing: 'Đang xử lý...',
    actions: 'Hành động',
    date: 'Ngày',
    time: 'Giờ',
    status: 'Trạng thái',
    mentor: 'Giảng viên',
    student: 'Học viên',
    next: 'Tiếp theo',
    submit: 'Nộp bài',
    viewAll: 'Xem tất cả',
    enterClass: 'Vào lớp học'
  },
  auth: {
    login: 'Đăng nhập',
    register: 'Đăng ký',
    emailLabel: 'Email của bạn',
    passwordLabel: 'Mật khẩu',
    fullNameLabel: 'Họ và tên',
    forgotPassword: 'Quên mật khẩu?',
    enterSystem: 'Vào hệ thống',
    readme: 'Hướng dẫn hệ thống (README)',
    registerSuccess: 'Đăng ký thành công! Đang chuyển hướng...',
    loginFailed: 'Đăng nhập thất bại. Vui lòng thử lại.',
    emailNotExists: 'Email không tồn tại.',
    quickAccess: 'Lối tắt kiểm thử',
    quickAccessTitle: 'Lối tắt nhanh',
    newTeammate: 'Thành viên mới?',
    administrators: 'Quản trị viên',
    otherRoles: 'Vai trò khác',
    adminLabel: 'Quản trị',
    resetData: 'Khởi tạo lại dữ liệu',
    resetConfirm: 'Bạn có chắc chắn muốn xóa toàn bộ dữ liệu và khôi phục tài khoản mẫu?',
    chooseRole: 'Bạn muốn đăng ký là...',
    menteeRole: 'Học viên',
    mentorRole: 'Giảng viên (Mentor)',
    providerRole: 'Cộng tác viên (Affiliate)',
    completeRegister: 'Hoàn tất đăng ký'
  },
  mentee: {
    welcome: 'Chào mừng trở lại',
    readyToLearn: 'Bạn đã sẵn sàng cho buổi học tiếng Anh tiếp theo chưa?',
    creditsRemaining: 'tín chỉ còn lại',
    bookLesson: 'Đặt lịch học',
    nextLesson: 'Buổi học tiếp theo',
    noUpcoming: 'Không có buổi học nào sắp tới.',
    viewDetails: 'Xem chi tiết',
    weeklyGoal: 'Mục tiêu tuần',
    lessons: 'Buổi học',
    homework: 'Bài tập',
    pending: 'Cần làm',
    streak: 'Chuỗi',
    days: 'ngày',
    homeworkTitle: 'Bài tập về nhà',
    noHomework: 'Chưa có bài tập nào.',
    allDone: 'Đã hoàn thành hết!',
    submit: 'Nộp bài',
    wallet: 'Ví của tôi',
    availableBalance: 'Số dư khả dụng',
    topUp: 'Nạp thêm',
    transactionHistory: 'Lịch sử giao dịch',
    noTransactions: 'Chưa có giao dịch nào.',
    liveSupport: 'Hỗ trợ trực tuyến',
    credits: 'Tín chỉ',
    creditsLabel: 'Tín chỉ',
    creditsDescription: '* Tín chỉ được sử dụng cho các buổi học 1:1.',

    findMentorTitle: 'Tìm Mentor',
    findMentorSubtitle: 'Tìm kiếm và đặt lịch với các chuyên gia tiếng Anh hàng đầu.',
    searchPlaceholder: 'Tìm theo tên hoặc thẻ...',
    filters: 'Bộ lọc',
    viewProfile: 'Xem hồ sơ',
    perHour: '/ giờ',

    myBookings: 'Lịch học của tôi',
    buySubscription: 'Mua gói học',
    bookNewLesson: 'Đặt lịch mới',
    noBookingsTitle: 'Chưa có lịch học',
    noBookingsDesc: 'Hãy bắt đầu bằng việc tìm một mentor phù hợp.',
    scheduled: 'Sắp tới',
    completed: 'Hoàn thành',
    cancelled: 'Đã hủy',
    rescheduled: 'Đã dời',
    tabUpcoming: 'Sắp tới',
    tabHistory: 'Lịch sử',
    tabCancelled: 'Đã hủy',
    bookingsEmptyDesc: 'Các buổi học đã đặt sẽ hiển thị ở đây.',
    joinDetails: 'Tham gia / Chi tiết',
    rate: 'Đánh giá',
    underReview: 'Đang xem xét',
    reportSubmitted: 'Báo cáo đã được gửi. Đội ngũ của chúng tôi sẽ xem xét sớm.',
    allLevels: 'Tất cả cấp độ',
    allTopics: 'Tất cả chủ đề',
    mentorsAvailable: '{count} mentor khả dụng',
    recommended: 'Đề xuất',
    noMentorsFound: 'Không tìm thấy mentor',
    noMentorsDesc: 'Chúng tôi không tìm thấy mentor nào phù hợp với bộ lọc hiện tại.',
    bookingCancelledSuccess: 'Hủy lịch thành công. Buổi học hoặc tín chỉ đã được hoàn trả.',
    rescheduledSuccess: 'Dời lịch thành công.',
    loadingLessonDetails: 'Đang tải chi tiết buổi học...',
    backToBookings: 'Quay lại lịch học',
    lessonSummary: 'Tóm tắt buổi học',
    lessonDate: 'Ngày học',
    timeSlot: 'Khung giờ',
    sessionsLeft: 'Buổi học còn lại',
    assignmentLabel: 'Bài tập cho buổi học này',
    openTask: 'Mở bài tập',
    result: 'Kết quả',
    enterClassroom: 'Vào lớp học trực tuyến',
    reschedule: 'Dời lịch',
    cancelLesson: 'Hủy buổi học',
    leaveReview: 'Để lại đánh giá',
    pricingCalculationError: 'Không thể tính giá địa phương.',
    loadingProfile: 'Đang tải hồ sơ...',
    mentorNotFound: 'Không tìm thấy mentor.',
    yourRate: 'Giá của giáo viên',
    creditsPerSession: 'Tín chỉ/buổi',
    bookNow: 'Đặt ngay',
    aboutMe: 'Giới thiệu',
    mentorSchedule: 'Lịch của Mentor',
    timezoneConversion: 'Giờ đã được chuyển đổi sang múi giờ địa phương ({tz})',
    activeSubscription: 'Gói học đang hoạt động',
    planLabel: 'Gói học',
    mentorLabel: 'Mentor: ',
    expiresLabel: 'Hết hạn: ',
    activeStatus: 'ĐANG HOẠT ĐỘNG',
    subscriptionFailed: 'Đăng ký thất bại: ',
    loadingSubscription: 'Đang tải gói học...',
    noActiveSubscription: 'Không có gói học đang hoạt động',
    payAsYouGoPlan: 'Bạn đang sử dụng hình thức trả theo buổi học.',
    browsePlans: 'Xem các gói',
    usageStats: 'Thống kê sử dụng',
    totalSessions: 'Tổng số buổi',
    remaining: 'Còn lại',
    viewSchedule: 'Xem lịch',
    subscriptionDetailTitle: 'Chi tiết gói học',

    upgradeTitle: 'Nâng cấp Premium',
    upgradeDesc: 'Cam kết lộ trình học tập với gói tháng. Học đều đặn và tiết kiệm tới 20% so với mua lẻ.',
    premiumLearning: 'Học tập cao cấp',
    whySubscribe: 'Tại sao chọn gói đăng ký?',
    benefitTitle1: 'Cam kết học tập',
    benefitDesc1: 'Xây dựng thói quen nhất quán với lịch cố định hàng tuần. Không còn trì hoãn.',
    benefitTitle2: 'Giá tốt hơn',
    benefitDesc2: 'Tiết kiệm tới 20% so với đặt từng buổi học bằng tín chỉ.',
    benefitTitle3: 'Đảm bảo khung giờ',
    benefitDesc3: 'Giữ chỗ khung giờ vàng của mentor yêu thích trước 4 tuần.',
    faqTitle: 'Câu hỏi thường gặp',
    faq1Question: 'Tôi có thể dời lịch không?',
    faq1Answer: 'Có, tùy thuộc vào gói của bạn. Gói Starter cho phép 1 lần dời mỗi tháng. Phải hủy trước 24h.',
    faq2Question: 'Điều gì xảy ra nếu mentor hủy?',
    faq2Answer: 'Nếu mentor hủy, tín chỉ buổi học sẽ được hoàn trả vào hạn mức và bạn có thể đặt lại bất cứ lúc nào.',
    faq3Question: 'Tín chỉ có được chuyển sang tháng sau không?',
    faq3Answer: 'Không, các buổi học trong gói phải được sử dụng trong chu kỳ 4 tuần để khuyến khích luyện tập đều đặn.',
    activePlan: 'Bạn đang dùng gói {plan}',
    validUntil: 'Hết hạn ngày',
    manageSubscription: 'Quản lý gói',
    choosePlan: 'Chọn gói này',
    currentPlan: 'Gói hiện tại',
    lessonsCount: '{count} Buổi học',
    weeklySchedule: 'Lịch cố định hàng tuần',
    quotaInfo: '{cancel} lần hủy / {reschedule} lần dời',

    subscriptionDetail: {
      backToList: 'Quay lại danh sách gói',
      stepMentor: 'Chọn Mentor',
      stepSchedule: 'Lên lịch học',
      stepConfirm: 'Hoàn tất',
      mentorTitle: 'Người dẫn dắt hành trình',
      mentorSubtitle: 'Chọn một Mentor sẽ đồng hành cùng bạn trong suốt lộ trình 4 tuần.',
      scheduleTitle: 'Thiết lập thời gian biểu',
      scheduleSubtitle: 'Chọn khung giờ bạn sẽ học định kỳ hàng tuần.',
      projectedSchedule: 'Lịch học dự kiến',
      noSlots: 'Chưa chọn khung giờ',
      recurring: 'Định kỳ',
      nextStep: 'Xem tóm tắt & Thanh toán',
      summaryTitle: 'Xác nhận đăng ký',
      summarySubtitle: 'Vui lòng kiểm tra lại thông tin gói học của bạn.',
      planLabel: 'Gói học',
      sessionsSuffix: 'Buổi học / 4 tuần',
      mentorLabel: 'Mentor đồng hành',
      roadmapLabel: 'Lộ trình 4 tuần tới',
      totalPrice: 'Tổng cộng',
      walletBalance: 'Ví hiện tại',
      confirmAndStart: 'Xác nhận & Bắt đầu học',
      insufficientTitle: 'Số dư không đủ',
      insufficientDesc: 'Bạn cần thêm {needed} Credits để thực hiện giao dịch này.',
      topUpNow: 'Nạp thêm ngay'
    },

    calendar: {
      title: 'Chọn {count} khung giờ mỗi tuần',
      selectedCount: 'Đã chọn: {current} / {max}',
      note: '* Khung giờ xanh là lúc Mentor rảnh. Chọn lịch học định kỳ của bạn.'
    },

    activeSubscriptionTitle: 'Gói học đang kích hoạt',
    subscriptionLimits: 'Giới hạn gói',
    cancelsLeft: 'Lượt hủy còn lại',
    reschedulesLeft: 'Lượt dời còn lại',
    resetsOn: 'Làm mới vào',
    noActiveSub: 'Không có gói học',
    payAsYouGo: 'Bạn đang sử dụng chế độ thanh toán theo buổi.',

    profile: {
      myProfile: 'Hồ sơ của tôi',
      settings: 'Cài đặt',
      generalInformation: 'Thông tin chung',
      security: 'Bảo mật',
      changePassword: 'Đổi mật khẩu',
      currentPassword: 'Mật khẩu hiện tại',
      newPassword: 'Mật khẩu mới',
      confirmNewPassword: 'Xác nhận mật khẩu mới',
      updatePassword: 'Cập nhật mật khẩu',
      changeAvatar: 'Nhấn vào ảnh để thay đổi',
      saveChanges: 'Lưu thay đổi',
      saving: 'Đang lưu...',
      fullName: 'Họ và tên',
      email: 'Email',
      phone: 'Số điện thoại',
      country: 'Quốc gia'
    },

    walletModal: {
      title: 'Nạp Credits',
      paymentTotal: 'Tổng thanh toán',
      securePayment: 'Thanh toán bảo mật SSL',
      payNow: 'Thanh toán ngay',
      currency: 'Tiền tệ',
      selectCreditPackage: '1. Chọn gói Credits',
      credits: 'credits',
      selectPaymentMethod: '2. Chọn phương thức thanh toán',
      noPaymentMethods: 'Không có phương thức thanh toán. Vui lòng liên hệ admin.',
      choosePaymentMethod: 'Chọn phương thức thanh toán...',
      completePayment: '3. Hoàn tất thanh toán',
      totalAmount: 'Tổng số tiền',
      qrCode: 'Mã QR',
      bank: 'Ngân hàng',
      account: 'Số tài khoản',
      name: 'Tên chủ tài khoản',
      transactionCode: 'Mã giao dịch',
      transactionCodePlaceholder: 'Nhập mã giao dịch từ thanh toán của bạn',
      transactionCodeHint: 'Nhập mã giao dịch từ thanh toán {method} của bạn',
      processing: 'Đang xử lý...',
      confirmPayment: 'Xác nhận thanh toán',
      fillAllFields: 'Vui lòng điền đầy đủ các trường bắt buộc',
      transactionCodeTooShort: 'Mã giao dịch phải có ít nhất 6 ký tự',
      failedLoadMethods: 'Không thể tải phương thức thanh toán',
      failedCreateTopup: 'Không thể tạo giao dịch nạp tiền'
    },

    homeworkModal: {
      placeholder: 'Viết ghi chú hoặc câu trả lời của bạn...',
      dropzone: 'Kéo thả file bài làm vào đây',
      submitNow: 'Nộp bài ngay',
      submittedTitle: 'Bài tập đã nộp',
      submittedDesc: 'Đã nộp ngày {date}. Vui lòng chờ phản hồi.'
    }
  },
  notifications: {
    commissionEarned: 'Nhận hoa hồng!',
    commissionMessage: (amount: number, menteeName: string, topupAmount: number) =>
      `Bạn nhận được ${amount} Cr hoa hồng từ giao dịch nạp ${topupAmount} Cr của ${menteeName}.`,
    accountActivated: 'Tài khoản đã kích hoạt',
    accountActivatedMessage: 'Tài khoản của bạn đã được kích hoạt. Bạn có thể sử dụng tất cả tính năng.',
    accountSuspended: 'Tài khoản bị tạm ngưng',
    accountSuspendedMessage: (reason?: string) =>
      `Tài khoản của bạn đã bị tạm ngưng${reason ? `: ${reason}` : '.'}`,
    pendingApproval: 'Chờ phê duyệt',
    pendingApprovalMessage: 'Tài khoản của bạn đang chờ phê duyệt. Chúng tôi sẽ thông báo sau khi xem xét.',
    accountRejected: 'Tài khoản bị từ chối',
    accountRejectedMessage: (reason?: string) =>
      `Đơn đăng ký tài khoản của bạn đã bị từ chối${reason ? `: ${reason}` : '.'}`,
    newBooking: 'Lịch học mới!',
    newBookingMessage: (menteeName: string, startTime: string) =>
      `${menteeName} đã đặt lịch học vào lúc ${new Date(startTime).toLocaleString('vi-VN')}`,
    lessonCompleted: 'Buổi học đã hoàn thành',
    lessonCompletedMessage: (mentorName: string) =>
      `Buổi học với ${mentorName} đã hoàn thành. Vui lòng để lại đánh giá!`,
    bookingCancelled: 'Lịch học đã hủy',
    bookingCancelledMessage: (mentorName: string) =>
      `Lịch học với ${mentorName} đã bị hủy. Credits đã được hoàn trả.`,
    bookingRescheduled: 'Lịch học đã đổi',
    bookingRescheduledMessage: (mentorName: string) =>
      `Buổi học với ${mentorName} đã được dời lịch.`,
    payoutApproved: 'Yêu cầu rút tiền đã duyệt',
    payoutApprovedMessage: (amount: number) =>
      `Yêu cầu rút ${amount} Cr đã được phê duyệt và sẽ sớm được xử lý.`,
    payoutCompleted: 'Rút tiền thành công',
    payoutCompletedMessage: (amount: number) =>
      `${amount} Cr đã được chuyển. Vui lòng kiểm tra phương thức thanh toán.`,
    payoutRejected: 'Yêu cầu rút tiền bị từ chối',
    payoutRejectedMessage: (note?: string) =>
      `Yêu cầu rút tiền đã bị từ chối${note ? `: ${note}` : '.'}`,
    creditsAdded: 'Đã nạp Credits',
    creditsAddedMessage: (amount: number) =>
      `${amount} credits đã được thêm vào ví.`,
    refundReceived: 'Đã hoàn tiền',
    refundReceivedMessage: (amount: number, note?: string) =>
      `Bạn nhận được hoàn tiền ${amount} credits${note ? `: ${note}` : '.'}`,
    earningsReceived: 'Đã nhận thu nhập',
    earningsReceivedMessage: (amount: number) =>
      `Bạn nhận được ${amount} credits từ buổi học đã hoàn thành.`,
    creditAdjustment: 'Điều chỉnh Credits',
    creditAdjustmentMessage: (note?: string) =>
      `Quản trị viên đã điều chỉnh số dư${note ? `: ${note}` : '.'}`,
    subscriptionExpiring: 'Gói học sắp hết hạn',
    subscriptionExpiringMessage: (planName: string, expiryDate: string) =>
      `Gói ${planName} của bạn hết hạn vào ${new Date(expiryDate).toLocaleDateString('vi-VN')}. Gia hạn ngay!`,
    justNow: 'Vừa xong',
    minutesAgo: (minutes: number) => `${minutes} phút trước`,
    hoursAgo: (hours: number) => `${hours} giờ trước`,
    daysAgo: (days: number) => `${days} ngày trước`
  }
};

// Chinese (Simplified) - 简体中文
const zh = {
  nav: {
    dashboard: '仪表板',
    findMentor: '找导师',
    bookings: '我的课程',
    subscriptions: '订阅',
    homework: '作业',
    messages: '消息',
    wallet: '钱包',
    profile: '个人资料',
    signOut: '登出'
  },
  common: {
    loading: '加载中...',
    back: '返回',
    confirm: '确认',
    cancel: '取消',
    save: '保存',
    processing: '处理中...',
    actions: '操作',
    date: '日期',
    time: '时间',
    status: '状态',
    mentor: '导师',
    student: '学生',
    next: '下一步',
    submit: '提交',
    viewAll: '查看全部',
    enterClass: '进入课堂'
  },
  auth: {
    login: '登录',
    register: '注册',
    emailLabel: '您的邮箱',
    passwordLabel: '密码',
    fullNameLabel: '全名',
    forgotPassword: '忘记密码?',
    enterSystem: '进入系统',
    readme: '系统文档',
    registerSuccess: '注册成功！重定向中...',
    loginFailed: '登录失败，请重试。',
    emailNotExists: '邮箱不存在。',
    quickAccess: '开发环境访问',
    quickAccessTitle: '快速访问',
    newTeammate: '新队友？',
    administrators: '管理员',
    otherRoles: '其他角色',
    adminLabel: '管理',
    resetData: '重置系统数据',
    resetConfirm: '确定要清除所有数据并重新初始化吗?',
    chooseRole: '我想注册为...',
    menteeRole: '学生',
    mentorRole: '教学导师',
    providerRole: '推荐提供商',
    completeRegister: '完成注册'
  },
  mentee: {
    welcome: '欢迎回来',
    readyToLearn: '准备好参加下一堂英文课了吗?',
    creditsRemaining: '剩余积分',
    bookLesson: '预订课程',
    nextLesson: '下一堂课',
    noUpcoming: '没有已安排的课程。',
    viewDetails: '查看详情',
    weeklyGoal: '周目标',
    lessons: '课程',
    homework: '作业',
    pending: '待处理',
    streak: '连续天数',
    days: '天',
    homeworkTitle: '作业与练习',
    noHomework: '还没有分配作业。',
    allDone: '全部完成!',
    submit: '提交',
    wallet: '我的钱包',
    availableBalance: '可用余额',
    topUp: '充值',
    transactionHistory: '交易历史',
    noTransactions: '还没有交易。',
    liveSupport: '在线支持',
    credits: '积分',
    creditsLabel: '积分',
    creditsDescription: '* 积分用于1:1课程。',
    findMentorTitle: '找一位导师',
    findMentorSubtitle: '浏览并预订顶级英文专家的课程。',
    searchPlaceholder: '搜索名字或标签...',
    filters: '筛选',
    viewProfile: '查看资料',
    perHour: '/ 小时',
    myBookings: '我的课程',
    buySubscription: '购买订阅',
    bookNewLesson: '预订新课程',
    noBookingsTitle: '还没有课程',
    noBookingsDesc: '通过找导师开始您的学习之旅。',
    scheduled: '已安排',
    completed: '已完成',
    cancelled: '已取消',
    rescheduled: '已改期',
    tabUpcoming: '即将到来',
    tabHistory: '历史',
    tabCancelled: '已取消',
    bookingsEmptyDesc: '您预订的课程将显示在这里。',
    joinDetails: '加入 / 详情',
    rate: '评分',
    underReview: '审核中',
    reportSubmitted: '报告已提交。我们的团队将尽快审核。',
    allLevels: '所有级别',
    allTopics: '所有主题',
    mentorsAvailable: '{count} 位导师可用',
    recommended: '推荐',
    noMentorsFound: '未找到导师',
    noMentorsDesc: '我们找不到符合当前筛选条件的导师。',
    bookingCancelledSuccess: '预订已成功取消。课程或积分已退还。',
    rescheduledSuccess: '改期成功。',
    loadingLessonDetails: '加载课程详情中...',
    backToBookings: '返回我的课程',
    lessonSummary: '课程摘要',
    lessonDate: '上课日期',
    timeSlot: '时间段',
    sessionsLeft: '剩余课程',
    assignmentLabel: '本课程作业',
    openTask: '打开任务',
    result: '结果',
    enterClassroom: '进入在线教室',
    reschedule: '改期',
    cancelLesson: '取消课程',
    leaveReview: '留下评价',
    pricingCalculationError: '无法计算本地定价。',
    loadingProfile: '加载资料中...',
    mentorNotFound: '未找到导师。',
    yourRate: '教师费率',
    creditsPerSession: '积分/课',
    bookNow: '立即预订',
    aboutMe: '关于我',
    mentorSchedule: '导师时间表',
    timezoneConversion: '时间已转换为您的本地时区 ({tz})',
    activeSubscription: '活跃订阅',
    planLabel: '计划',
    mentorLabel: '导师: ',
    expiresLabel: '到期: ',
    activeStatus: '活跃',
    subscriptionFailed: '订阅失败: ',
    loadingSubscription: '加载订阅中...',
    noActiveSubscription: '无活跃订阅',
    payAsYouGoPlan: '您目前使用的是按次付费计划。',
    browsePlans: '浏览计划',
    usageStats: '使用统计',
    totalSessions: '总课程数',
    remaining: '剩余',
    viewSchedule: '查看时间表',
    subscriptionDetailTitle: '订阅详情',
    upgradeTitle: '升级至高级',
    upgradeDesc: '通过月度计划提交您的学习之旅。持续练习，相比积分套餐可节省20%。',
    premiumLearning: '高级学习',
    whySubscribe: '为什么选择订阅?',
    benefitTitle1: '承诺机制',
    benefitDesc1: '通过每周固定时间表培养持续习惯。不再拖延课程。',
    benefitTitle2: '更优惠的价格',
    benefitDesc2: '与使用积分预订单节课相比，最多可节省20%。',
    benefitTitle3: '保证时段',
    benefitDesc3: '提前4周锁定您最喜欢的导师的黄金时段。',
    faqTitle: '常见问题',
    faq1Question: '我可以改期吗?',
    faq1Answer: '可以，这取决于您的计划。入门计划每月允许改期1次。必须提前24小时取消。',
    faq2Question: '如果导师取消会怎样?',
    faq2Answer: '如果导师取消，课程积分将退还到您的配额，您可以随时重新预订。',
    faq3Question: '积分会结转吗?',
    faq3Answer: '不会，订阅课程必须在4周周期内使用，以鼓励持续练习。',
    activePlan: '您有一个活跃的{plan}订阅',
    validUntil: '有效期至',
    manageSubscription: '管理订阅',
    choosePlan: '选择计划',
    currentPlan: '当前计划',
    lessonsCount: '{count}堂课',
    weeklySchedule: '每周固定时间表',
    quotaInfo: '{cancel}取消 / {reschedule}改期',
    profile: {
      myProfile: '我的资料',
      settings: '设置',
      generalInformation: '基本信息',
      security: '安全',
      changePassword: '更改密码',
      currentPassword: '当前密码',
      newPassword: '新密码',
      confirmNewPassword: '确认新密码',
      updatePassword: '更新密码',
      changeAvatar: '点击相机图标更改',
      saveChanges: '保存更改',
      saving: '保存中...',
      fullName: '全名',
      email: '邮箱',
      phone: '电话',
      country: '国家'
    },
    subscriptionDetail: {
      backToList: '返回计划',
      stepMentor: '选择导师',
      stepSchedule: '设置时间',
      stepConfirm: '确认',
      mentorTitle: '您的学习指南',
      mentorSubtitle: '选择一位导师陪伴您度过接下来的4周。',
      scheduleTitle: '设定您的每周计划',
      scheduleSubtitle: '选择适合您每周日程的时间段。',
      projectedSchedule: '预计日程',
      noSlots: '未选择时间',
      recurring: '重复',
      nextStep: '摘要与付款',
      summaryTitle: '确认订阅',
      summarySubtitle: '请检查您的计划详情。',
      planLabel: '学习计划',
      sessionsSuffix: '堂课 / 4周',
      mentorLabel: '学习伙伴',
      roadmapLabel: '接下来的4周学习计划',
      totalPrice: '总价格',
      walletBalance: '当前钱包',
      confirmAndStart: '确认并开始学习',
      insufficientTitle: '余额不足',
      insufficientDesc: '您需要再充值{needed}积分才能完成注册。',
      topUpNow: '立即充值'
    },
    calendar: {
      title: '选择每周{count}个固定时间',
      selectedCount: '已选择: {current} / {max}',
      note: '* 绿色时间段表示导师有效。选择每周重复的时间。'
    },
    activeSubscriptionTitle: '活跃订阅',
    subscriptionLimits: '订阅限制',
    cancelsLeft: '取消次数',
    reschedulesLeft: '改期次数',
    resetsOn: '重置于',
    noActiveSub: '没有活跃订阅',
    payAsYouGo: '您目前使用的是随用随付计划。',
    walletModal: {
      title: '充值积分',
      paymentTotal: '总付款',
      securePayment: '安全SSL付款',
      payNow: '立即支付',
      currency: '货币',
      selectCreditPackage: '1. 选择积分套餐',
      credits: '积分',
      selectPaymentMethod: '2. 选择支付方式',
      noPaymentMethods: '没有可用的支付方式。请联系管理员。',
      choosePaymentMethod: '选择支付方式...',
      completePayment: '3. 完成支付',
      totalAmount: '总金额',
      qrCode: '二维码',
      bank: '银行',
      account: '账户',
      name: '账户名称',
      transactionCode: '交易代码',
      transactionCodePlaceholder: '输入您的支付交易代码',
      transactionCodeHint: '输入您的{method}支付交易代码',
      processing: '处理中...',
      confirmPayment: '确认支付',
      fillAllFields: '请填写所有必填字段',
      transactionCodeTooShort: '交易代码必须至少6个字符',
      failedLoadMethods: '无法加载支付方式',
      failedCreateTopup: '无法创建充值交易'
    },
    homeworkModal: {
      placeholder: '在此输入您的笔记或答案...',
      dropzone: '将文件拖放到此处',
      submitNow: '立即提交',
      submittedTitle: '作业已提交',
      submittedDesc: '您在{date}提交了此作业。等待反馈。'
    }
  }
};

// Korean - 한국어
const ko = {
  nav: {
    dashboard: '대시보드',
    findMentor: '멘토 찾기',
    bookings: '내 수업',
    subscriptions: '구독',
    homework: '숙제',
    messages: '메시지',
    wallet: '지갑',
    profile: '프로필',
    signOut: '로그아웃'
  },
  common: {
    loading: '로딩중...',
    back: '뒤로가기',
    confirm: '확인',
    cancel: '취소',
    save: '저장',
    processing: '처리중...',
    actions: '작업',
    date: '날짜',
    time: '시간',
    status: '상태',
    mentor: '멘토',
    student: '학생',
    next: '다음',
    submit: '제출',
    viewAll: '모두 보기',
    enterClass: '수업 입장'
  },
  auth: {
    login: '로그인',
    register: '가입',
    emailLabel: '이메일',
    passwordLabel: '비밀번호',
    fullNameLabel: '전체 이름',
    forgotPassword: '비밀번호를 잊으셨나요?',
    enterSystem: '시스템 입장',
    readme: '시스템 설명서',
    registerSuccess: '가입 성공! 리다이렉트중...',
    loginFailed: '로그인 실패. 다시 시도하세요.',
    emailNotExists: '이메일이 존재하지 않습니다.',
    quickAccess: '개발 접근',
    resetData: '시스템 데이터 초기화',
    resetConfirm: '모든 데이터를 삭제하고 다시 초기화 하시겠습니까?',
    chooseRole: '다음으로 가입하고 싶습니다...',
    menteeRole: '학생',
    mentorRole: '교육 멘토',
    providerRole: '제휴 제공자',
    completeRegister: '가입 완료',
    quickAccessTitle: '빠른 접근',
    newTeammate: '새 팀원?',
    administrators: '관리자',
    otherRoles: '다른 역할',
    adminLabel: '관리'
  },
  mentee: {
    welcome: '다시 오셨습니다',
    readyToLearn: '다음 영어 수업을 할 준비가 되셨나요?',
    creditsRemaining: '남은 크레딧',
    bookLesson: '수업 예약',
    nextLesson: '다음 수업',
    noUpcoming: '예약된 수업이 없습니다.',
    viewDetails: '상세보기',
    weeklyGoal: '주간 목표',
    lessons: '수업',
    homework: '숙제',
    pending: '대기중',
    streak: '연속일수',
    days: '일',
    homeworkTitle: '숙제 & 과제',
    noHomework: '아직 할당된 숙제가 없습니다.',
    allDone: '모두 완료!',
    submit: '제출',
    wallet: '내 지갑',
    availableBalance: '사용 가능 잔액',
    topUp: '충전',
    transactionHistory: '거래 내역',
    noTransactions: '아직 거래가 없습니다.',
    liveSupport: '실시간 지원',
    credits: '크레딧',
    creditsLabel: '크레딧',
    creditsDescription: '* 크레딧은 1:1 수업에 사용됩니다.',
    findMentorTitle: '멘토 찾기',
    findMentorSubtitle: '최고의 영어 전문가의 수업을 둘러보고 예약하세요.',
    searchPlaceholder: '이름 또는 태그 검색...',
    filters: '필터',
    viewProfile: '프로필 보기',
    perHour: '/ 시간',
    myBookings: '내 수업',
    buySubscription: '구독 구매',
    bookNewLesson: '새 수업 예약',
    noBookingsTitle: '아직 수업이 없습니다',
    noBookingsDesc: '멘토를 찾아 학습을 시작하세요.',
    scheduled: '예약됨',
    completed: '완료',
    cancelled: '취소됨',
    rescheduled: '연기됨',
    tabUpcoming: '예정',
    tabHistory: '기록',
    tabCancelled: '취소됨',
    bookingsEmptyDesc: '예약된 수업이 여기에 표시됩니다.',
    joinDetails: '참가 / 세부정보',
    rate: '평가',
    underReview: '검토 중',
    reportSubmitted: '보고서가 제출되었습니다. 팀에서 곧 검토할 예정입니다.',
    allLevels: '모든 레벨',
    allTopics: '모든 주제',
    mentorsAvailable: '{count}명의 멘토 이용 가능',
    recommended: '추천',
    noMentorsFound: '멘토를 찾을 수 없습니다',
    noMentorsDesc: '현재 필터 조건에 맞는 멘토를 찾을 수 없습니다.',
    bookingCancelledSuccess: '예약이 성공적으로 취소되었습니다. 수업 또는 크레딧이 반환되었습니다.',
    rescheduledSuccess: '성공적으로 연기되었습니다.',
    loadingLessonDetails: '수업 세부정보 로딩 중...',
    backToBookings: '내 수업으로 돌아가기',
    lessonSummary: '수업 요약',
    lessonDate: '수업 날짜',
    timeSlot: '시간대',
    sessionsLeft: '남은 수업',
    assignmentLabel: '이 수업의 과제',
    openTask: '과제 열기',
    result: '결과',
    enterClassroom: '온라인 교실 입장',
    reschedule: '연기',
    cancelLesson: '수업 취소',
    leaveReview: '리뷰 남기기',
    pricingCalculationError: '현지 가격을 계산할 수 없습니다.',
    loadingProfile: '프로필 로딩 중...',
    mentorNotFound: '멘토를 찾을 수 없습니다.',
    yourRate: '선생님 요금',
    creditsPerSession: '크레딧/세션',
    bookNow: '지금 예약',
    aboutMe: '소개',
    mentorSchedule: '멘토 일정',
    timezoneConversion: '시간이 현지 시간대로 변환되었습니다 ({tz})',
    activeSubscription: '활성 구독',
    planLabel: '계획',
    mentorLabel: '멘토: ',
    expiresLabel: '만료: ',
    activeStatus: '활성',
    subscriptionFailed: '구독 실패: ',
    loadingSubscription: '구독 로딩 중...',
    noActiveSubscription: '활성 구독 없음',
    payAsYouGoPlan: '현재 사용량에 따라 지불하는 플랜을 사용하고 있습니다.',
    browsePlans: '플랜 둘러보기',
    usageStats: '사용 통계',
    totalSessions: '총 세션',
    remaining: '남은',
    viewSchedule: '일정 보기',
    subscriptionDetailTitle: '구독 세부정보',
    upgradeTitle: '프리미엄으로 업그레이드',
    upgradeDesc: '월간 계획으로 학습 여정을 강화하세요. 지속적인 연습을 받고 크레딧 팩에 비해 최대 20% 절약하세요.',
    premiumLearning: '프리미엄 학습',
    whySubscribe: '구독을 선택하는 이유는?',
    benefitTitle1: '학습 약속',
    benefitDesc1: '주간 고정 일정으로 꾸준한 습관을 형성하세요. 더 이상 수업을 미루지 마세요.',
    benefitTitle2: '더 나은 가격',
    benefitDesc2: '크레딧으로 단일 수업을 예약하는 것에 비해 최대 20% 절약하세요.',
    benefitTitle3: '보장된 시간',
    benefitDesc3: '좋아하는 멘토의 프라임 타임을 4주 전에 확보하세요.',
    faqTitle: '자주 묻는 질문',
    faq1Question: '수업을 연기할 수 있나요?',
    faq1Answer: '예, 플랜에 따라 다릅니다. 스타터 플랜은 월 1회 연기를 허용합니다. 취소는 24시간 전에 해야 합니다.',
    faq2Question: '멘토가 취소하면 어떻게 되나요?',
    faq2Answer: '멘토가 취소하면 수업 크레딧이 할당량으로 반환되며 언제든지 다시 예약할 수 있습니다.',
    faq3Question: '크레딧이 이월되나요?',
    faq3Answer: '아니요, 구독 수업은 4주 주기 내에 사용해야 꾸준한 연습을 장려할 수 있습니다.',
    activePlan: '{plan} 활성 구독이 있습니다',
    validUntil: '유효 기간',
    manageSubscription: '구독 관리',
    choosePlan: '계획 선택',
    currentPlan: '현재 계획',
    lessonsCount: '{count}수업',
    weeklySchedule: '주간 고정 일정',
    quotaInfo: '{cancel}취소 / {reschedule}연기',
    profile: {
      myProfile: '내 프로필',
      settings: '설정',
      generalInformation: '일반 정보',
      security: '보안',
      changePassword: '비밀번호 변경',
      currentPassword: '현재 비밀번호',
      newPassword: '새 비밀번호',
      confirmNewPassword: '새 비밀번호 확인',
      updatePassword: '비밀번호 업데이트',
      changeAvatar: '카메라 아이콘을 클릭하여 변경',
      saveChanges: '변경사항 저장',
      saving: '저장중...',
      fullName: '전체 이름',
      email: '이메일',
      phone: '전화',
      country: '국가'
    },
    subscriptionDetail: {
      backToList: '계획으로 돌아가기',
      stepMentor: '멘토 선택',
      stepSchedule: '시간 설정',
      stepConfirm: '확인',
      mentorTitle: '학습 가이드',
      mentorSubtitle: '다음 4주 동안 함께할 멘토를 선택하세요.',
      scheduleTitle: '주간 일정 설정',
      scheduleSubtitle: '당신의 주간 일정에 맞는 시간을 선택하세요.',
      projectedSchedule: '예상 일정',
      noSlots: '선택된 시간 없음',
      recurring: '반복',
      nextStep: '요약 및 결제',
      summaryTitle: '구독 확인',
      summarySubtitle: '계획 상세정보를 검토하세요.',
      planLabel: '학습 계획',
      sessionsSuffix: '수업 / 4주',
      mentorLabel: '학습 파트너',
      roadmapLabel: '향후 4주 학습 계획',
      totalPrice: '총 가격',
      walletBalance: '현재 지갑',
      confirmAndStart: '확인 & 학습 시작',
      insufficientTitle: '잔액 부족',
      insufficientDesc: '등록을 완료하려면 {needed}개의 크레딧이 더 필요합니다.',
      topUpNow: '지금 충전'
    },
    calendar: {
      title: '주당 {count}개의 고정 시간 선택',
      selectedCount: '선택됨: {current} / {max}',
      note: '* 녹색 시간은 멘토 가능 시간입니다. 매주 반복되는 시간을 선택하세요.'
    },
    activeSubscriptionTitle: '활성 구독',
    subscriptionLimits: '구독 제한',
    cancelsLeft: '취소 횟수',
    reschedulesLeft: '연기 횟수',
    resetsOn: '초기화 날짜',
    noActiveSub: '활성 구독 없음',
    payAsYouGo: '현재 종량제 계획을 사용 중입니다.',
    walletModal: {
      title: '크레딧 충전',
      paymentTotal: '총 결제',
      securePayment: '안전한 SSL 결제',
      payNow: '지금 결제',
      currency: '통화',
      selectCreditPackage: '1. 크레딧 패키지 선택',
      credits: '크레딧',
      selectPaymentMethod: '2. 결제 방법 선택',
      noPaymentMethods: '사용 가능한 결제 방법이 없습니다. 관리자에게 문의하세요.',
      choosePaymentMethod: '결제 방법 선택...',
      completePayment: '3. 결제 완료',
      totalAmount: '총액',
      qrCode: 'QR 코드',
      bank: '은행',
      account: '계좌',
      name: '예금주',
      transactionCode: '거래 코드',
      transactionCodePlaceholder: '결제 거래 코드를 입력하세요',
      transactionCodeHint: '{method} 결제 거래 코드를 입력하세요',
      processing: '처리 중...',
      confirmPayment: '결제 확인',
      fillAllFields: '모든 필수 필드를 입력하세요',
      transactionCodeTooShort: '거래 코드는 최소 6자 이상이어야 합니다',
      failedLoadMethods: '결제 방법을 불러올 수 없습니다',
      failedCreateTopup: '충전 거래를 생성할 수 없습니다'
    },
    homeworkModal: {
      placeholder: '여기에 노트나 답변을 작성하세요...',
      dropzone: '파일을 여기에 드래그 앤 드롭',
      submitNow: '지금 제출',
      submittedTitle: '과제 제출됨',
      submittedDesc: '{date}에 이 과제를 제출했습니다. 피드백을 기다리고 있습니다.'
    }
  },
  notifications: {
    commissionEarned: '수수료 획득!',
    commissionMessage: (amount: number, menteeName: string, topupAmount: number) =>
      `${menteeName}의 ${topupAmount} Cr 충전에서 ${amount} Cr 수수료를 받았습니다.`,
    accountActivated: '계정 활성화됨',
    accountActivatedMessage: '계정이 활성화되었습니다. 이제 모든 기능을 사용할 수 있습니다.',
    accountSuspended: '계정 정지됨',
    accountSuspendedMessage: (reason?: string) =>
      `계정이 정지되었습니다${reason ? `: ${reason}` : '.'}`,
    pendingApproval: '승인 대기 중',
    pendingApprovalMessage: '계정이 승인 대기 중입니다. 검토 후 알려드리겠습니다.',
    accountRejected: '계정 거부됨',
    accountRejectedMessage: (reason?: string) =>
      `계정 신청이 거부되었습니다${reason ? `: ${reason}` : '.'}`,
    newBooking: '새 예약!',
    newBookingMessage: (menteeName: string, startTime: string) =>
      `${menteeName}님이 ${new Date(startTime).toLocaleString('ko-KR')}에 수업을 예약했습니다`,
    lessonCompleted: '수업 완료',
    lessonCompletedMessage: (mentorName: string) =>
      `${mentorName}님과의 수업이 완료되었습니다. 리뷰를 남겨주세요!`,
    bookingCancelled: '예약 취소됨',
    bookingCancelledMessage: (mentorName: string) =>
      `${mentorName}님과의 예약이 취소되었습니다. 크레딧이 반환되었습니다.`,
    bookingRescheduled: '예약 변경됨',
    bookingRescheduledMessage: (mentorName: string) =>
      `${mentorName}님과의 수업이 변경되었습니다.`,
    payoutApproved: '출금 승인됨',
    payoutApprovedMessage: (amount: number) =>
      `${amount} Cr 출금이 승인되었으며 곧 처리됩니다.`,
    payoutCompleted: '출금 완료',
    payoutCompletedMessage: (amount: number) =>
      `${amount} Cr이 전송되었습니다. 결제 방법을 확인하세요.`,
    payoutRejected: '출금 거부됨',
    payoutRejectedMessage: (note?: string) =>
      `출금 요청이 거부되었습니다${note ? `: ${note}` : '.'}`,
    creditsAdded: '크레딧 충전됨',
    creditsAddedMessage: (amount: number) =>
      `${amount} 크레딧이 지갑에 추가되었습니다.`,
    refundReceived: '환불 완료',
    refundReceivedMessage: (amount: number, note?: string) =>
      `${amount} 크레딧 환불을 받았습니다${note ? `: ${note}` : '.'}`,
    earningsReceived: '수익 받음',
    earningsReceivedMessage: (amount: number) =>
      `완료된 수업에서 ${amount} 크레딧을 받았습니다.`,
    creditAdjustment: '크레딧 조정',
    creditAdjustmentMessage: (note?: string) =>
      `관리자가 잔액을 조정했습니다${note ? `: ${note}` : '.'}`,
    subscriptionExpiring: '구독 만료 임박',
    subscriptionExpiringMessage: (planName: string, expiryDate: string) =>
      `${planName} 구독이 ${new Date(expiryDate).toLocaleDateString('ko-KR')}에 만료됩니다. 지금 갱신하세요!`,
    justNow: '방금',
    minutesAgo: (minutes: number) => `${minutes}분 전`,
    hoursAgo: (hours: number) => `${hours}시간 전`,
    daysAgo: (days: number) => `${days}일 전`
  }
};

// Japanese - 日本語
const ja = {
  nav: {
    dashboard: 'ダッシュボード',
    findMentor: 'メンターを探す',
    bookings: '私のレッスン',
    subscriptions: 'サブスクリプション',
    homework: '宿題',
    messages: 'メッセージ',
    wallet: 'ウォレット',
    profile: 'プロフィール',
    signOut: 'ログアウト'
  },
  common: {
    loading: '読み込み中...',
    back: '戻る',
    confirm: '確認',
    cancel: 'キャンセル',
    save: '保存',
    processing: '処理中...',
    actions: 'アクション',
    date: '日付',
    time: '時間',
    status: 'ステータス',
    mentor: 'メンター',
    student: '学生',
    next: '次へ',
    submit: '送信',
    viewAll: 'すべて表示',
    enterClass: 'クラスに入る'
  },
  auth: {
    login: 'ログイン',
    register: '登録',
    emailLabel: 'メールアドレス',
    passwordLabel: 'パスワード',
    fullNameLabel: '姓名',
    forgotPassword: 'パスワードをお忘れですか?',
    enterSystem: 'システムに入る',
    readme: 'システムドキュメント',
    registerSuccess: '登録成功!リダイレクト中...',
    loginFailed: 'ログイン失敗。もう一度試してください。',
    emailNotExists: 'メールアドレスが存在しません。',
    quickAccess: '開発アクセス',
    resetData: 'システムデータをリセット',
    resetConfirm: 'すべてのデータをクリアして再初期化してもよろしいですか?',
    chooseRole: 'として登録したい...',
    menteeRole: '学生',
    mentorRole: '教育メンター',
    providerRole: 'アフィリエイトプロバイダー',
    completeRegister: '登録を完了'
  },
  mentee: {
    welcome: 'おかえりなさい',
    readyToLearn: '次の英語レッスンの準備はできていますか?',
    creditsRemaining: '残りクレジット',
    bookLesson: 'レッスンを予約',
    nextLesson: '次のレッスン',
    noUpcoming: 'スケジュールされたレッスンはありません。',
    viewDetails: '詳細を表示',
    weeklyGoal: '週間目標',
    lessons: 'レッスン',
    homework: '宿題',
    pending: '保留中',
    streak: '連続日数',
    days: '日',
    homeworkTitle: '宿題と課題',
    noHomework: 'まだ宿題が割り当てられていません。',
    allDone: 'すべて完了!',
    submit: '提出',
    wallet: '私のウォレット',
    availableBalance: '利用可能なバランス',
    topUp: 'チャージ',
    transactionHistory: '取引履歴',
    noTransactions: 'まだ取引はありません。',
    liveSupport: 'ライブサポート',
    credits: 'クレジット',
    creditsLabel: 'クレジット',
    creditsDescription: '* クレジットは1:1セッションに使用されます。',
    findMentorTitle: 'メンターを探す',
    findMentorSubtitle: 'トップの英語専門家のレッスンを閲覧して予約します。',
    searchPlaceholder: '名前またはタグを検索...',
    filters: 'フィルター',
    viewProfile: 'プロフィールを表示',
    perHour: '/ 時間',
    myBookings: '私のレッスン',
    buySubscription: 'サブスクリプションを購入',
    bookNewLesson: '新しいレッスンを予約',
    noBookingsTitle: 'まだレッスンはありません',
    noBookingsDesc: 'メンターを探して学習を開始します。',
    scheduled: 'スケジュール済み',
    completed: '完了',
    cancelled: 'キャンセル',
    rescheduled: '日程を変更',
    tabUpcoming: '予定',
    tabHistory: '履歴',
    tabCancelled: 'キャンセル済み',
    bookingsEmptyDesc: '予約したレッスンがここに表示されます。',
    joinDetails: '参加 / 詳細',
    rate: '評価',
    underReview: 'レビュー中',
    reportSubmitted: 'レポートが送信されました。チームがまもなくレビューします。',
    allLevels: 'すべてのレベル',
    allTopics: 'すべてのトピック',
    mentorsAvailable: '{count}人のメンター利用可能',
    recommended: 'おすすめ',
    noMentorsFound: 'メンターが見つかりません',
    noMentorsDesc: '現在のフィルターに一致するメンターが見つかりませんでした。',
    bookingCancelledSuccess: '予約が正常にキャンセルされました。セッションまたはクレジットが返却されました。',
    rescheduledSuccess: '日程変更が成功しました。',
    loadingLessonDetails: 'レッスン詳細を読み込み中...',
    backToBookings: 'マイレッスンに戻る',
    lessonSummary: 'レッスン概要',
    lessonDate: 'レッスン日',
    timeSlot: 'タイムスロット',
    sessionsLeft: '残りセッション',
    assignmentLabel: 'このレッスンの課題',
    openTask: 'タスクを開く',
    result: '結果',
    enterClassroom: 'オンライン教室に入る',
    reschedule: '日程変更',
    cancelLesson: 'レッスンをキャンセル',
    leaveReview: 'レビューを残す',
    pricingCalculationError: 'ローカル価格を計算できません。',
    loadingProfile: 'プロフィールを読み込み中...',
    mentorNotFound: 'メンターが見つかりません。',
    yourRate: '教師の料金',
    creditsPerSession: 'クレジット/セッション',
    bookNow: '今すぐ予約',
    aboutMe: '自己紹介',
    mentorSchedule: 'メンタースケジュール',
    timezoneConversion: '時間はあなたのローカルゾーンに変換されています ({tz})',
    activeSubscription: 'アクティブなサブスクリプション',
    planLabel: 'プラン',
    mentorLabel: 'メンター: ',
    expiresLabel: '有効期限: ',
    activeStatus: 'アクティブ',
    subscriptionFailed: 'サブスクリプション失敗: ',
    loadingSubscription: 'サブスクリプション読み込み中...',
    noActiveSubscription: 'アクティブなサブスクリプションなし',
    payAsYouGoPlan: '現在、従量課金プランを使用しています。',
    browsePlans: 'プランを閲覧',
    usageStats: '使用統計',
    totalSessions: '合計セッション',
    remaining: '残り',
    viewSchedule: 'スケジュールを表示',
    subscriptionDetailTitle: 'サブスクリプション詳細',
    upgradeTitle: 'プレミアムにアップグレード',
    upgradeDesc: '月額プランで学習の旅を強化します。継続的な練習を受け、クレジットパックと比較して最大20%節約します。',
    premiumLearning: 'プレミアム学習',
    whySubscribe: 'サブスクリプションを選択する理由は?',
    benefitTitle1: '学習コミットメント',
    benefitDesc1: '毎週固定スケジュールで一貫した習慣を築きます。もうレッスンを先延ばしにしません。',
    benefitTitle2: 'より良い料金',
    benefitDesc2: 'クレジットで単一レッスンを予約するのと比較して最大20%節約できます。',
    benefitTitle3: '保証されたスロット',
    benefitDesc3: 'お気に入りのメンターのプライムタイムスロットを4週間前に確保します。',
    faqTitle: 'よくある質問',
    faq1Question: 'レッスンの日程を変更できますか?',
    faq1Answer: 'はい、プランによって異なります。スタータープランでは月1回の日程変更が可能です。キャンセルは24時間前に行う必要があります。',
    faq2Question: 'メンターがキャンセルした場合はどうなりますか?',
    faq2Answer: 'メンターがキャンセルした場合、セッションクレジットが割り当てに返却され、いつでも再予約できます。',
    faq3Question: 'クレジットは繰り越されますか?',
    faq3Answer: 'いいえ、サブスクリプションセッションは4週間のサイクル内で使用する必要があり、継続的な練習を促進します。',
    activePlan: '{plan}アクティブなサブスクリプションがあります',
    validUntil: '有効期限',
    manageSubscription: 'サブスクリプション管理',
    choosePlan: 'プランを選択',
    currentPlan: '現在のプラン',
    lessonsCount: '{count}レッスン',
    weeklySchedule: '週間固定スケジュール',
    quotaInfo: '{cancel}キャンセル / {reschedule}延期',
    profile: {
      myProfile: '私のプロフィール',
      settings: '設定',
      generalInformation: '一般情報',
      security: 'セキュリティ',
      changePassword: 'パスワードを変更',
      currentPassword: '現在のパスワード',
      newPassword: '新しいパスワード',
      confirmNewPassword: '新しいパスワードを確認',
      updatePassword: 'パスワードを更新',
      changeAvatar: 'カメラアイコンをクリックして変更',
      saveChanges: '変更を保存',
      saving: '保存中...',
      fullName: '姓名',
      email: 'メールアドレス',
      phone: '電話',
      country: '国'
    },
    subscriptionDetail: {
      backToList: 'プランに戻る',
      stepMentor: 'メンターを選択',
      stepSchedule: 'スケジュールを設定',
      stepConfirm: '確認',
      mentorTitle: '学習ガイド',
      mentorSubtitle: '次の4週間付き添うメンターを選択します。',
      scheduleTitle: '週間ルーチンを設定',
      scheduleSubtitle: '週間スケジュールに適したスロットを選択します。',
      projectedSchedule: '予定スケジュール',
      noSlots: '選択されたスロットなし',
      recurring: '繰り返し',
      nextStep: '概要と支払い',
      summaryTitle: 'サブスクリプションを確認',
      summarySubtitle: 'プランの詳細を確認してください。',
      planLabel: '学習計画',
      sessionsSuffix: 'レッスン / 4週間',
      mentorLabel: '学習パートナー',
      roadmapLabel: '今後4週間の学習ロードマップ',
      totalPrice: '総価格',
      walletBalance: '現在のウォレット',
      confirmAndStart: '確認して学習を開始',
      insufficientTitle: '残高不足',
      insufficientDesc: '登録を完了するには、{needed}個のクレジットが必要です。',
      topUpNow: '今すぐチャージ'
    },
    calendar: {
      title: '週{count}個の固定スロットを選択',
      selectedCount: '選択済み: {current} / {max}',
      note: '* 緑色のスロットはメンターの利用可能時間を示します。毎週繰り返す時間を選択します。'
    },
    activeSubscriptionTitle: 'アクティブなサブスクリプション',
    subscriptionLimits: 'サブスクリプション制限',
    cancelsLeft: 'キャンセル回数',
    reschedulesLeft: '延期回数',
    resetsOn: 'リセット日',
    noActiveSub: 'アクティブなサブスクリプションなし',
    payAsYouGo: '現在、従量課金制プランを使用しています。',
    walletModal: {
      title: 'クレジットをチャージ',
      paymentTotal: '合計支払い',
      securePayment: '安全なSSL決済',
      payNow: '今すぐ支払う',
      currency: '通貨',
      selectCreditPackage: '1. クレジットパッケージを選択',
      credits: 'クレジット',
      selectPaymentMethod: '2. 支払い方法を選択',
      noPaymentMethods: '利用可能な支払い方法がありません。管理者にお問い合わせください。',
      choosePaymentMethod: '支払い方法を選択...',
      completePayment: '3. 支払いを完了',
      totalAmount: '合計金額',
      qrCode: 'QRコード',
      bank: '銀行',
      account: '口座',
      name: '口座名義',
      transactionCode: '取引コード',
      transactionCodePlaceholder: '支払いの取引コードを入力',
      transactionCodeHint: '{method}支払いの取引コードを入力してください',
      processing: '処理中...',
      confirmPayment: '支払いを確認',
      fillAllFields: 'すべての必須フィールドを入力してください',
      transactionCodeTooShort: '取引コードは少なくとも6文字である必要があります',
      failedLoadMethods: '支払い方法を読み込めませんでした',
      failedCreateTopup: 'チャージ取引を作成できませんでした'
    },
    homeworkModal: {
      placeholder: 'ここにメモまたはアンサーを入力...',
      dropzone: 'ファイルをここにドラッグアンドドロップ',
      submitNow: '今すぐ提出',
      submittedTitle: '課題が提出されました',
      submittedDesc: '{date}にこの課題を提出しました。フィードバックを待機中です。'
    }
  },
  notifications: {
    commissionEarned: 'コミッション獲得！',
    commissionMessage: (amount: number, menteeName: string, topupAmount: number) =>
      `${menteeName}の${topupAmount} Crチャージから${amount} Crコミッションを獲得しました。`,
    accountActivated: 'アカウントが有効化されました',
    accountActivatedMessage: 'アカウントが有効化されました。すべての機能を使用できます。',
    accountSuspended: 'アカウント停止',
    accountSuspendedMessage: (reason?: string) =>
      `アカウントが停止されました${reason ? `：${reason}` : '。'}`,
    pendingApproval: '承認待ち',
    pendingApprovalMessage: 'アカウントは承認待ちです。審査後にお知らせします。',
    accountRejected: 'アカウント拒否',
    accountRejectedMessage: (reason?: string) =>
      `アカウント申請が拒否されました${reason ? `：${reason}` : '。'}`,
    newBooking: '新しい予約！',
    newBookingMessage: (menteeName: string, startTime: string) =>
      `${menteeName}が${new Date(startTime).toLocaleString('ja-JP')}にレッスンを予約しました`,
    lessonCompleted: 'レッスン完了',
    lessonCompletedMessage: (mentorName: string) =>
      `${mentorName}とのレッスンが完了しました。レビューを残してください！`,
    bookingCancelled: '予約キャンセル',
    bookingCancelledMessage: (mentorName: string) =>
      `${mentorName}との予約がキャンセルされました。クレジットが返金されました。`,
    bookingRescheduled: '予約変更',
    bookingRescheduledMessage: (mentorName: string) =>
      `${mentorName}とのレッスンが変更されました。`,
    payoutApproved: '出金承認',
    payoutApprovedMessage: (amount: number) =>
      `${amount} Crの出金が承認され、まもなく処理されます。`,
    payoutCompleted: '出金完了',
    payoutCompletedMessage: (amount: number) =>
      `${amount} Crが送金されました。お支払い方法を確認してください。`,
    payoutRejected: '出金拒否',
    payoutRejectedMessage: (note?: string) =>
      `出金リクエストが拒否されました${note ? `：${note}` : '。'}`,
    creditsAdded: 'クレジット追加',
    creditsAddedMessage: (amount: number) =>
      `${amount}クレジットがウォレットに追加されました。`,
    refundReceived: '返金完了',
    refundReceivedMessage: (amount: number, note?: string) =>
      `${amount}クレジットの返金を受け取りました${note ? `：${note}` : '。'}`,
    earningsReceived: '収益受領',
    earningsReceivedMessage: (amount: number) =>
      `完了したレッスンから${amount}クレジットを獲得しました。`,
    creditAdjustment: 'クレジット調整',
    creditAdjustmentMessage: (note?: string) =>
      `管理者が残高を調整しました${note ? `：${note}` : '。'}`,
    subscriptionExpiring: 'サブスクリプション期限切れ間近',
    subscriptionExpiringMessage: (planName: string, expiryDate: string) =>
      `${planName}サブスクリプションは${new Date(expiryDate).toLocaleDateString('ja-JP')}に期限切れになります。今すぐ更新してください！`,
    justNow: 'たった今',
    minutesAgo: (minutes: number) => `${minutes}分前`,
    hoursAgo: (hours: number) => `${hours}時間前`,
    daysAgo: (days: number) => `${days}日前`
  }
};

// Export translations with Chinese, Korean, and Japanese support
export const translations: Record<Language, any> = {
  en,
  vi,
  zh, // ✅ Chinese (Simplified)
  ko, // ✅ Korean
  ja  // ✅ Japanese
};
